// Stage 15: Advanced Memory Module — typed frontend client.
//
// Step-2 surface is intentionally narrow: only the settings round-trip is
// exercised by the enable-switch UI. The richer list / ingest / forget /
// recall / export / migrate helpers below compile cleanly but are unused
// until Steps 3–6 flesh out the MemoryAdvTab UI.

import { apiGet, apiJson } from './client';

export type MemoryType = 'fact' | 'event' | 'instruction' | 'task';

export interface MemoryAdvRecord {
  id: string;
  type: MemoryType;
  content: string;
  topic_key: string | null;
  persona_id: string | null;
  session_id: string;
  event_timestamp: number | null;
  ingested_at: number;
  ingest_id: string;
  superseded_by: string | null;
  forgotten_at: number | null;
  search_queries_json: string;
  source_turn_indices: string | null;
}

export interface ListMemoriesResponse {
  memories: MemoryAdvRecord[];
  nextCursor: string | null;
}

export interface IngestResponse {
  ingestId: string;
  messagesSeen: number;
  memoriesCreated: number;
  memoriesSuperseded: number;
  memoriesDropped: number;
  durationMs: number;
  vectorizationQueued: boolean;
}

export interface RecallResponse {
  queryId: string;
  result: string;
  candidates?: Array<{
    memoryId: string;
    content: string;
    type: MemoryType;
    rrfScore: number;
    channelHits: string[];
  }>;
  durationMs: number;
}

export async function listMemories(params: {
  type?: MemoryType;
  sessionId?: string;
  includeSuperseded?: boolean;
  includeForgotten?: boolean;
  cursor?: string;
  limit?: number;
}): Promise<ListMemoriesResponse> {
  const q = new URLSearchParams();
  if (params.type) q.set('type', params.type);
  if (params.sessionId) q.set('sessionId', params.sessionId);
  if (params.includeSuperseded) q.set('includeSuperseded', 'true');
  if (params.includeForgotten) q.set('includeForgotten', 'true');
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return apiGet(`/api/memory-adv/memories${qs ? `?${qs}` : ''}`);
}

export const forgetMemory = (memoryId: string) =>
  apiJson<{ forgotten: boolean; memoryId: string }>(
    'POST',
    '/api/memory-adv/forget',
    { memoryId },
  );

export const ingestConversation = (conversationId: string) =>
  apiJson<IngestResponse>('POST', '/api/memory-adv/ingest', {
    conversationId,
  });

export const recallMemory = (query: string) =>
  apiJson<RecallResponse>('POST', '/api/memory-adv/recall', { query });

export const migrateLegacy = () =>
  apiJson<{
    migrated: boolean;
    alreadyUpToDate: boolean;
    memoryId?: string;
    reason?: string;
  }>('POST', '/api/memory-adv/migrate-legacy');

export function exportHref(): string {
  const base = import.meta.env.VITE_API_BASE as string;
  return `${base}/api/memory-adv/export?format=ndjson`;
}
